import { parseTag } from "@/features/machines/addressing";
import type {
  ProductionHistoryPoint,
  ProductionHistoryWindow,
} from "@/features/devices/types";
import { widgetCapabilities } from "./capabilities";
import { clampCells } from "./cellGrid";
import { isNativeCumulativeSource } from "./productionSources";
import { isTypeCompatible } from "./widgetCompatibility";
import type { GhmSignalKind } from "@ghm/mqtt-contract";
import type {
  ChartKind,
  ChartSeries,
  DashboardWidget,
  WidgetLayout,
  WidgetSettings,
  WidgetType,
} from "./types";

export interface ResolvedChartConfig {
  kind: ChartKind;
  series: ChartSeries;
  window: ProductionHistoryWindow;
  yScale: "auto" | "fixed";
}

/** Sinal de origem de um source real (`G0_P1_acc`) ou derivado. */
export function signalOfSource(source: string): "s" | "acc" | "v" | null {
  const tag = parseTag(source);
  if (tag) {
    return tag.signal === "s" || tag.signal === "acc" || tag.signal === "v"
      ? tag.signal
      : null;
  }
  const match = /_(s|acc|v)$/i.exec(source);
  return match ? (match[1].toLowerCase() as "s" | "acc" | "v") : null;
}

/**
 * Só sinais numéricos viram gráfico no v0: `v` (ritmo) e `acc` (produção/
 * acumulado). Estado digital (`s`) fica para uma fase posterior, pois usa
 * outra fonte (`digital_snapshots`), não os continuous aggregates de porta.
 */
export function isChartableSource(source: string): boolean {
  const signal = signalOfSource(source);
  return signal === "v" || signal === "acc";
}

/**
 * Default de gráfico por sinal (decisão do dono, 23/06):
 *  - `v`   → linha de ritmo (velocidade no tempo);
 *  - `acc` → barras de produção por período (delta por bucket).
 */
export function defaultChartForSignal(signal: "acc" | "v"): {
  kind: ChartKind;
  series: ChartSeries;
} {
  return signal === "acc"
    ? { kind: "bar", series: "throughput" }
    : { kind: "line", series: "rate" };
}

/**
 * Config efetiva do gráfico, com fallback para widgets legados (sem
 * `settings.chart`): respeita o `widgetType` salvo para a forma e deriva a
 * série pelo sinal; janela cai em `history.window` ou 24h.
 */
export function resolveChartConfig(widget: DashboardWidget): ResolvedChartConfig {
  const explicit = widget.settings.chart;
  const signal = signalOfSource(widget.source);
  const base = defaultChartForSignal(signal === "acc" ? "acc" : "v");
  const kindFromType: ChartKind | undefined =
    widget.widgetType === "bar-chart"
      ? "bar"
      : widget.widgetType === "line-chart" || widget.widgetType === "multi-chart"
        ? "line"
        : undefined;
  return {
    kind: explicit?.kind ?? kindFromType ?? base.kind,
    series: explicit?.series ?? base.series,
    window: explicit?.window ?? widget.settings.history?.window ?? "24h",
    yScale: explicit?.yScale ?? "auto",
  };
}

/** Valor plotado por ponto conforme a série. `null` = sem dado. */
export function chartPointValue(
  point: ProductionHistoryPoint,
  series: ChartSeries,
): number | null {
  if (series === "rate") return point.vAvg ?? point.vLast;
  if (series === "cumulative") return point.accLast;
  return point.delta;
}

/**
 * Por que um bucket não tem valor plotável. A distinção é operacional, não
 * cosmética:
 *
 *  - `failed`  — o bucket TEVE amostras (`sampleCount > 0`) e nenhuma trouxe
 *    valor. Em `AIN` é falha elétrica (NE43 `s=0`) e em `MB` é transação fora
 *    de `ok` (timeout, quadro inválido, exceção): o firmware OMITE `v` de
 *    propósito, e o worker persiste a linha com `v` nulo e o `s` da falha.
 *    O equipamento estava sendo lido e não respondeu — isso é um fato, e a
 *    linha precisa mostrar o buraco.
 *  - `missing` — não existe bucket nenhum no intervalo: não houve telemetria.
 *
 * Antes desta separação os dois casos eram o MESMO no gráfico: o ponto era
 * descartado com `flatMap` e a linha ligava os dois lados como se a leitura
 * fosse contínua. Um timeout de 40 minutos virava uma reta suave.
 */
export type ChartPointGapReason = "failed" | "missing";

/**
 * Projeta a série do histórico preservando os buracos. Diferente do antigo
 * `flatMap` que os removia, um bucket sem valor vira um ponto com
 * `value: null` — cada renderer decide como expressá-lo (a linha interrompe,
 * a barra vira marca discreta na base).
 */
export function projectChartPoints(
  points: ProductionHistoryPoint[],
  series: ChartSeries,
  window: ProductionHistoryWindow,
): Array<{
  at: string;
  value: number | null;
  unavailable?: ChartPointGapReason;
  gap?: boolean;
  includesGapDelta?: boolean;
  min?: number | null;
  max?: number | null;
}> {
  return points.map((point, index, all) => {
    const projected = chartPointValue(point, series);
    const previous = all[index - 1];
    const gap = previous ? isGapBefore(previous.bucket, point.bucket, window) : false;

    if (projected !== null) {
      // A serie de ritmo plota a MEDIA do bucket; os extremos viajam junto
      // para o tooltip nao esconder a variacao dentro da hora/minuto.
      return series === "rate"
        ? { at: point.bucket, value: projected, gap, min: point.vMin, max: point.vMax }
        : { at: point.bucket, value: projected, gap, ...(series === "throughput" && gap ? { includesGapDelta: true } : {}) };
    }

    // `sampleCount > 0` é o que separa "leu e falhou" de um bucket vazio que
    // por algum motivo chegou na série. Sem amostra não há o que afirmar.
    return {
      at: point.bucket,
      value: null,
      unavailable: point.sampleCount > 0 ? ("failed" as const) : ("missing" as const),
      gap,
    };
  });
}

/** Tamanho do bucket (ms) por janela — resolução derivada (1m/1h/1d). */
export function bucketMsForWindow(window: ProductionHistoryWindow): number {
  if (window === "6h" || window === "12h" || window === "24h") return 60_000;
  if (window === "30d") return 86_400_000;
  return 3_600_000; // 48h e 7d usam buckets de 1 hora
}

/** Duracao da janela em ms. Espelha `resolveProductionHistoryPlan` da API. */
export function windowDurationMs(window: ProductionHistoryWindow): number {
  const hours: Record<ProductionHistoryWindow, number> = {
    "6h": 6, "12h": 12, "24h": 24, "48h": 48, "7d": 7 * 24, "30d": 30 * 24,
  };
  return hours[window] * 3_600_000;
}

/**
 * Há um salto de telemetria entre dois buckets consecutivos? Usa 1,8× o
 * bucket como folga para não acusar um único bucket faltante. Um bucket logo
 * após um gap concentra a produção do período sem telemetria (atribuição
 * temporal imprecisa) — por isso é destacado no gráfico.
 */
export function isGapBefore(
  prevAt: string,
  at: string,
  window: ProductionHistoryWindow,
): boolean {
  const ms = new Date(at).getTime() - new Date(prevAt).getTime();
  return Number.isFinite(ms) && ms > 1.8 * bucketMsForWindow(window);
}

export function pointsHaveGap(
  points: Array<{ at: string }>,
  window: ProductionHistoryWindow,
): boolean {
  for (let index = 1; index < points.length; index += 1) {
    if (isGapBefore(points[index - 1].at, points[index].at, window)) return true;
  }
  return false;
}

/**
 * Ponto de tendência: instante + valor projetado.
 *  - `gap`: há salto de telemetria imediatamente antes (atribuição imprecisa);
 *  - `unknown`: o intervalo inclui período SEM telemetria cuja produção é
 *    desconhecida — o valor não deve empilhar nem inflar a escala; vira marca
 *    discreta na base.
 */
export interface TrendBar {
  at: string;
  /** `null` = sem leitura utilizável no intervalo. Ver `unavailable`. */
  value: number | null;
  gap?: boolean;
  /** Delta de contador atravessa lacuna; nao atribuir o incremento ao bucket da retomada. */
  includesGapDelta?: boolean;
  breakBefore?: boolean;
  unknown?: boolean;
  unavailable?: ChartPointGapReason;
  /**
   * Fim do intervalo que a barra cobre. So existe em bins de varios buckets:
   * sem ele o tooltip descreveria um bin de tres horas como um bucket de uma.
   */
  until?: string;
  min?: number | null;
  max?: number | null;
}

/**
 * Agrupa por faixas de TEMPO, com alvo `maxBars` bins. Quebras de qualidade ou
 * lacunas podem exceder esse alvo: nunca apagar evidencia para caber na tela.
 * `sum` para
 * produção por período (o delta soma no intervalo); `last` para séries de nível
 * (amostra o último valor do bin). Falha/ausencia/unknown ficam separados das
 * leituras validas. Compartilhado pelo gráfico (face) e pelo cartão herói.
 *
 * `bucketMs`, quando informado, fecha o intervalo do bin (`until`) no fim do
 * ultimo bucket que ele contem.
 */
export function binBars(
  points: TrendBar[],
  maxBars: number,
  mode: "sum" | "last",
  bucketMs?: number,
): TrendBar[] {
  if (points.length <= maxBars || !bucketMs || !Number.isFinite(bucketMs) || bucketMs <= 0 || maxBars < 1) return points;
  const intervals = points.map(point => ({ from: Date.parse(point.at), to: point.until ? Date.parse(point.until) : Date.parse(point.at) + bucketMs }));
  // Nao inventar duracao/ordem para dados que nao provam um intervalo valido.
  if (intervals.some((interval, i) => !Number.isFinite(interval.from) || !Number.isFinite(interval.to) ||
    interval.to <= interval.from || (i > 0 && interval.from < intervals[i - 1].to))) return points;
  const origin = intervals[0].from;
  const binMs = Math.max(bucketMs, Math.ceil((intervals.at(-1)!.to - origin) / maxBars / bucketMs) * bucketMs);
  const quality = (point: TrendBar) => point.unknown ? "unknown" : point.value === null ? point.unavailable ?? "missing" : "value";
  const bins: TrendBar[] = [];
  for (let index = 0; index < points.length;) {
    const start = index;
    const bin = Math.floor((intervals[start].from - origin) / binMs);
    index += 1;
    while (index < points.length && Math.floor((intervals[index].from - origin) / binMs) === bin &&
      intervals[index].from === intervals[index - 1].to && quality(points[index]) === quality(points[start]) &&
      !points[index].gap && !points[index].breakBefore && !points[index].includesGapDelta && !points[start].includesGapDelta) index += 1;
    const slice = points.slice(start, index);
    const first = slice[0];
    const last = slice[slice.length - 1];
    const value = first.value === null ? null : mode === "sum"
      ? slice.reduce((sum, point) => sum + (point.value as number), 0) : last.value;
    bins.push({
      ...first,
      value,
      until: new Date(intervals[index - 1].to).toISOString(),
      // `last` conserva os extremos da leitura realmente escolhida, nao os da primeira.
      ...(mode === "last" ? { min: last.min, max: last.max } : {}),
    });
  }
  return bins;
}

/**
 * Resumo da JANELA para series de ritmo/leitura (`v`): media, extremos e
 * cobertura. Calculado sobre os buckets ja carregados — nenhuma consulta nova.
 *
 * A media e ponderada por `sampleCount`: um bucket com 7 amostras nao pode
 * pesar o mesmo que um com 60. Buckets sem valor (`vAvg` nulo) ficam fora da
 * media E da cobertura — periodo sem telemetria nao vira zero.
 *
 * Limite conhecido: `sampleCount` conta todas as linhas do bucket, inclusive
 * leituras que falharam (`v` nulo em AIN/MB). Em encoder (`v` sempre
 * publicado) o peso e exato; em Modbus com timeouts intermitentes, buckets
 * mistos pesam um pouco a mais. Fechar isso exige `count(v)` nos continuous
 * aggregates.
 *
 * `coverage` e a fracao dos buckets esperados na janela que trouxeram valor —
 * nao e a cobertura em segundos da disponibilidade, que tolera microgaps.
 */
export interface RateWindowSummary {
  mean: number | null;
  min: number | null;
  max: number | null;
  /** 0..1: buckets com valor / buckets esperados entre `from` e `to`. */
  coverage: number;
}

export function summarizeRateWindow(history: {
  points: ProductionHistoryPoint[];
  window: ProductionHistoryWindow;
  from: string;
  to: string;
}): RateWindowSummary {
  let weighted = 0;
  let weight = 0;
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  let withValue = 0;

  for (const point of history.points) {
    const average = point.vAvg ?? point.vLast;
    if (average === null || !Number.isFinite(average)) continue;
    const samples = Math.max(1, point.sampleCount);
    weighted += average * samples;
    weight += samples;
    withValue += 1;
    const low = point.vMin ?? average;
    const high = point.vMax ?? average;
    if (low < min) min = low;
    if (high > max) max = high;
  }

  const bucketMs = bucketMsForWindow(history.window);
  const spanMs = Date.parse(history.to) - Date.parse(history.from);
  const expected = Number.isFinite(spanMs) && spanMs > 0 ? Math.ceil(spanMs / bucketMs) : 0;

  return {
    mean: weight > 0 ? weighted / weight : null,
    min: Number.isFinite(min) ? min : null,
    max: Number.isFinite(max) ? max : null,
    coverage: expected > 0 ? Math.min(1, withValue / expected) : 0,
  };
}

/**
 * Barras de "produção por período" a partir da série bruta de buckets: projeta
 * o `delta` de cada bucket e agrupa em `maxBars` bins somando o intervalo.
 *
 * Decisão do dono (25/06): durante um gap de telemetria NÃO sabemos a produção
 * do período — então o "catch-up" acumulado NÃO empilha: zera o valor e marca
 * `unknown`, virando marca discreta na base em vez de um pico que esmaga a
 * escala. Dois sinais de catch-up:
 *  1. salto de TEMPO antes do bucket (`isGapBefore`);
 *  2. delta fisicamente IMPOSSÍVEL — implica um ritmo muito acima do `vMax`
 *     observado na janela (o acc do equipamento contou durante o gap e
 *     despejou num bucket contíguo ao retomar).
 * Trade-off assumido: a soma das barras pode não bater com o total da janela
 * (o período desconhecido fica de fora).
 */
export function productionTrendBars(
  points: ProductionHistoryPoint[],
  window: ProductionHistoryWindow,
  maxBars = 48,
): TrendBar[] {
  const bucketMinutes = bucketMsForWindow(window) / 60_000;
  // Teto plausível de produção por bucket = maior ritmo observado × duração ×
  // folga. Só aplica quando há ritmo positivo observado; senão cai só no gap
  // temporal (não inventa teto a partir de zero).
  const maxRate = points.reduce(
    (max, point) => Math.max(max, point.vMax ?? point.vAvg ?? 0),
    0,
  );
  const ceiling = maxRate > 0 ? maxRate * bucketMinutes * 4 : Infinity;

  const raw: TrendBar[] = points.map((point, index, all) => {
    const previous = all[index - 1];
    const timeGap = previous ? isGapBefore(previous.bucket, point.bucket, window) : false;
    const implausible = point.delta > ceiling;
    return timeGap || implausible
      ? { at: point.bucket, value: 0, gap: timeGap, unknown: true }
      : { at: point.bucket, value: point.delta };
  });
  return binBars(raw, maxBars, "sum", bucketMsForWindow(window));
}

/**
 * Janela de um KPI de produção por JANELA HISTÓRICA (modo `total` com janela
 * definida e diferente de `current`) — a variante só-leitura que ganha o
 * cartão herói com a tendência embutida. `null` para as demais (ritmo,
 * projeção, acumulado zerável), que seguem no KPI legado.
 */
export function historicalProductionWindow(
  widget: DashboardWidget,
): ProductionHistoryWindow | null {
  if (widget.widgetType !== "production-kpi") return null;
  if (isNativeCumulativeSource(widget.source, widget.settings.signalKind)) return null;
  const production = widget.settings.production;
  if (production?.mode !== "total") return null;
  const window = production.window;
  return window && window !== "current" ? window : null;
}

/**
 * Retorna uma cópia do widget com a janela histórica trocada, gravada no campo
 * certo por tipo — é o que o seletor de período NO CARD persiste:
 *  - `line-chart`/`bar-chart` → `settings.chart.window` (materializando o
 *    `chart` efetivo de widgets legados que só tinham `history.window`);
 *  - `production-kpi` em modo total por janela → `settings.production.window`;
 *  - `utilization` → `settings.history.window`.
 * Tipos sem janela retornam o widget original (no-op defensivo).
 */
export function applyWidgetWindow(
  widget: DashboardWidget,
  window: ProductionHistoryWindow,
): DashboardWidget {
  // Tipo sem janela nao tem onde gravar: devolve o widget intacto.
  if (!widgetCapabilities(widget.widgetType).window) return widget;

  if (widget.widgetType !== "production-kpi" && widget.widgetType !== "utilization") {
    const chart = resolveChartConfig(widget);
    return {
      ...widget,
      settings: { ...widget.settings, chart: { ...chart, window } },
    };
  }
  if (
    widget.widgetType === "production-kpi" &&
    widget.settings.production?.mode === "total" &&
    widget.settings.production.window &&
    widget.settings.production.window !== "current"
  ) {
    return {
      ...widget,
      settings: {
        ...widget.settings,
        production: { ...widget.settings.production, window },
      },
    };
  }
  if (widget.widgetType === "utilization") {
    return {
      ...widget,
      settings: { ...widget.settings, history: { window } },
    };
  }
  return widget;
}

/**
 * Desfaz o "Gerar grafico": devolve a configuracao salva antes da troca.
 *
 * Nao restaura por adivinhacao — le `settings.chartOrigin`, gravado no momento
 * da substituicao. Sem essa marca nao ha volta possivel, e a funcao devolve o
 * widget intacto em vez de chutar um tipo.
 *
 * Graficos legados nao possuem snapshot completo. Neles restaura somente o
 * que a marca antiga prova, sem inventar secao, escala ou titulo do operador.
 */
export function revertChartWidget(widget: DashboardWidget): DashboardWidget {
  const origem = widget.settings.chartOrigin;
  if (!origem) return widget;

  if (origem.snapshot) {
    const snapshot = origem.snapshot;
    return {
      ...widget,
      title: origem.title,
      widgetType: origem.widgetType,
      source: snapshot.source,
      extraSources: snapshot.extraSources?.slice(),
      section: snapshot.section,
      settings: structuredClone(snapshot.settings),
      layout: { ...snapshot.layout },
    };
  }

  const { chart, chartOrigin, history, ...resto } = widget.settings;
  void chart;
  void chartOrigin;
  void history;

  return {
    ...widget,
    title: origem.title,
    widgetType: origem.widgetType,
    settings: {
      ...resto,
      // A janela que a ORIGEM tinha volta com ela. `history` e descartado por
      // padrao porque no grafico ele e do grafico — mas um widget legado
      // guardava a propria janela ali (ver `applyWidgetWindow`, que fala em
      // "widgets legados que so tinham `history.window`"), e devolver o card
      // sem ela seria perder configuracao no gesto que existe para ser
      // reversivel.
      ...(origem.history ? { history: origem.history } : null),
    },
    layout: {
      ...widget.layout,
      // Mesma razao da ida: o tipo de volta tem outro tamanho natural, e as
      // celulas do grafico deixariam o card com espaco vazio.
      w: undefined,
      h: undefined,
      sizeExplicit: undefined,
    },
  };
}

interface DeriveChartOptions {
  /** Id explícito (testes). Default: único por timestamp. */
  id?: string;
  /** Ordem no layout. Default: logo após o widget de origem. */
  order?: number;
  /** Relógio injetável para id determinístico em testes. */
  now?: number;
}

function chartOriginSnapshot(origin: DashboardWidget): NonNullable<WidgetSettings["chartOrigin"]> {
  const { chartOrigin, ...settings } = origin.settings;
  void chartOrigin;
  return {
    widgetType: origin.widgetType,
    title: origin.title,
    snapshot: {
      version: 1,
      source: origin.source,
      ...(origin.extraSources ? { extraSources: origin.extraSources.slice() } : null),
      ...(origin.section !== undefined ? { section: origin.section } : null),
      layout: { ...origin.layout },
      settings: structuredClone(settings),
    },
  };
}

/** Preserva a malha escolhida, mas um grafico nunca nasce abaixo do minimo. */
function chartLayout(origin: DashboardWidget, widgetType: WidgetType, order: number): WidgetLayout {
  const { w, h, sizeExplicit, ...layout } = origin.layout;
  void sizeExplicit;
  return {
    ...layout,
    order,
    ...(w !== undefined && h !== undefined ? clampCells({ w, h }, widgetType) : null),
  };
}

/**
 * Cria um widget de gráfico a partir de um widget numérico de origem,
 * herdando `source`/sinal, unidade e decimais. O tipo/série vêm do sinal.
 */
export function deriveChartWidget(
  origin: DashboardWidget,
  options: DeriveChartOptions = {},
): DashboardWidget {
  // DISPONIBILIDADE tem um grafico proprio, e nao e a serie do sinal.
  // Perguntar "qual a serie de um percentual de disponibilidade?" nao tem
  // resposta util: o que interessa e a LINHA DO TEMPO rodando x parado, que ja
  // existe como `utilization`. Por isso o grafico do indicador e o proprio
  // utilization, herdando a regra de disponibilidade que o operador ja
  // configurou — do contrario o card novo mediria outra coisa que o antigo.
  if (origin.widgetType === "availability") {
    const janela =
      origin.settings.availability?.window &&
      origin.settings.availability.window !== "current"
        ? origin.settings.availability.window
        : "24h";
    return {
      ...origin,
      id: options.id ?? `chart-utilization-${origin.source}-${(options.now ?? Date.now()).toString(36)}`,
      title: `Linha do tempo · ${origin.title}`,
      widgetType: "utilization",
      settings: {
        ...origin.settings,
        history: { window: janela },
        chartOrigin: chartOriginSnapshot(origin),
      },
      layout: chartLayout(origin, "utilization", options.order ?? origin.layout.order),
    };
  }

  const signal = signalOfSource(origin.source) === "acc" ? "acc" : "v";
  const { kind, series } = defaultChartForSignal(signal);
  const isMultiSource =
    origin.widgetType === "multi-value" && (origin.extraSources?.length ?? 0) > 0;
  const widgetType: WidgetType = isMultiSource
    ? "multi-chart"
    : kind === "bar"
      ? "bar-chart"
      : "line-chart";
  // Comparativo é SEMPRE linha: N barras no mesmo eixo se escondem umas atrás
  // das outras, e comparar é o produto do card. Sem forçar aqui, um comparativo
  // ancorado em `_acc` nascia `multi-chart` com `chart.kind: "bar"` gravado —
  // `resolveChartConfig` devolvia "bar" (o explícito vence o tipo) enquanto o
  // widget desenhava linha, deixando config e desenho em desacordo.
  const chartKind: ChartKind = isMultiSource ? "line" : kind;
  // Barras de produção por período leem melhor em buckets horários: o default
  // é 7d (1h/bucket), enquanto a linha de ritmo nasce em 24h. Uma janela
  // explícita da origem (KPI por janela) sempre vence.
  const fallbackWindow: ProductionHistoryWindow = kind === "bar" ? "7d" : "24h";
  const window: ProductionHistoryWindow =
    origin.settings.production?.window &&
    origin.settings.production.window !== "current"
      ? origin.settings.production.window
      : (origin.settings.history?.window ?? fallbackWindow);
  const prefix = isMultiSource ||
    (signal === "v" &&
      origin.settings.signalKind !== undefined &&
      origin.settings.signalKind !== "rate")
    ? "Histórico"
    : series === "throughput"
      ? "Produção"
      : series === "rate"
        ? "Ritmo"
        : "Acumulado";
  const now = options.now ?? Date.now();
  const unit =
    signal === "acc" && origin.settings.unit?.trim() === "%"
      ? undefined
      : origin.settings.unit;

  return {
    ...origin,
    id: options.id ?? `chart-${chartKind}-${origin.source}-${now.toString(36)}`,
    title: `${prefix} · ${origin.title}`,
    widgetType,
    settings: {
      ...origin.settings,
      unit,
      chart: { kind: chartKind, series, window, yScale: "auto" },
      chartOrigin: chartOriginSnapshot(origin),
    },
    layout: chartLayout(origin, widgetType, options.order ?? origin.layout.order + 1),
  };
}

/**
 * Este card pode virar grafico?
 *
 * A pergunta tem DUAS partes, e por muito tempo so a primeira era feita:
 *
 * 1. o TIPO do card admite a substituicao (`canGenerateChart`);
 * 2. o grafico resultante e compativel com a FONTE.
 *
 * A segunda faltava, e o gating usava `isChartableSource` — que olha o sufixo
 * da tag (`_v`/`_acc`). E exatamente a dimensao que o cabecalho da matriz
 * documenta como errada: o sufixo diz em que slot do envelope o numero viaja,
 * nao o que ele significa. Um `v` declarado `state` (codigo de falha, modo de
 * operacao) tem `line-chart` BLOQUEADO na matriz — media e tendencia de um enum
 * nao significam nada — e ainda assim o menu do card oferecia um clique que
 * criava esse mesmo `line-chart`. O menu era uma porta dos fundos para a regra
 * que o editor aplica.
 *
 * O tipo alvo vem de `deriveChartWidget`, e nao de uma tabela paralela: quem
 * autoriza e quem executa precisam ser a mesma resposta, senao voltam a
 * divergir.
 *
 * DISPONIBILIDADE segue isenta. O grafico dela nao e a serie do sinal ancora —
 * e a linha do tempo rodando x parado —, entao a compatibilidade da fonte com
 * `utilization` nao e a pergunta certa para ela.
 *
 * Sem `signalKind` a matriz cai no conjunto legado por sufixo, que e
 * permissivo de proposito: nao bloquear card que ja existe e funciona.
 */
export function podeGerarGrafico(
  widget: DashboardWidget,
  signalKind: GhmSignalKind | undefined,
): boolean {
  if (!widgetCapabilities(widget.widgetType).canGenerateChart) return false;
  if (widget.widgetType === "availability") return true;
  if (!isChartableSource(widget.source)) return false;
  const alvo = deriveChartWidget(widget, { id: "__sonda__", now: 0 }).widgetType;
  return isTypeCompatible(
    {
      source: widget.source,
      label: widget.title,
      kind: "signal",
      signal: signalOfSource(widget.source) ?? undefined,
      signalKind,
    },
    alvo,
  );
}
